import React from "react";
import { Link, useParams } from "react-router-dom";
import { useSEO, breadcrumbSchema } from "@/lib/seo";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { STORE, DISCLAIMER } from "@/lib/store";
import NotFound from "@/pages/NotFound";

const POLICIES = {
  "shipping-policy": {
    overline: "Policy",
    title: "Shipping Policy",
    description:
      "How Renova Peptide Co. prepares, packages, and ships research materials through our connected store.",
    sections: [
      { h: "Order Processing", p: "Orders are received and processed through our connected Shift4Shop store, which serves as the source of truth for pricing, inventory, taxes, and shipping options at checkout." },
      { h: "Packaging", p: "Research materials are prepared with discreet, protective packaging appropriate for their format. We handle every order with consistency and care." },
      { h: "Shipping Methods & Timing", p: "Available shipping methods, rates, and estimated timing are presented at checkout on our store. Please refer to the checkout page for the options applicable to your location." },
      { h: "Tracking", p: "Where tracking is available, it is provided through your store account and any confirmation communications associated with your order." },
      { h: "Questions", p: "For shipping questions, please use our Contact page with your order number, or sign in to your store account." },
    ],
  },
  "returns-policy": {
    overline: "Policy",
    title: "Returns & Refunds",
    description: "Our approach to returns and refunds for research materials.",
    sections: [
      { h: "Research Materials", p: "Because our products are research materials, returns are handled carefully and are subject to the return terms presented by our connected store at the time of purchase." },
      { h: "Damaged or Incorrect Items", p: "If an order arrives damaged or an incorrect item is received, please contact us promptly with your order number so we can help resolve the issue." },
      { h: "Refund Processing", p: "Approved refunds are processed through the original payment method via our store's checkout and payment provider." },
      { h: "How to Start", p: "To start a return or refund request, use our Contact page and include your order number and a description of the issue." },
    ],
  },
  "privacy-policy": {
    overline: "Legal",
    title: "Privacy Policy",
    description: "How Renova Peptide Co. handles the information you provide.",
    sections: [
      { h: "Information We Collect", p: "We collect the information you choose to provide — such as your name and email when you contact us or subscribe to updates — and information necessary to process orders through our connected store." },
      { h: "How We Use Information", p: "We use your information to respond to enquiries, send updates you have requested, and support order fulfilment. We do not sell your personal information." },
      { h: "Newsletter", p: "If you subscribe to updates, you may unsubscribe at any time. Your email is used only to send the communications you have consented to receive." },
      { h: "Store & Checkout", p: "Checkout, payment, and account data are processed by our connected Shift4Shop store in accordance with its own privacy and security practices." },
      { h: "Contact", p: "For privacy questions or requests regarding your information, please use our Contact page." },
    ],
  },
  "terms": {
    overline: "Legal",
    title: "Terms & Conditions",
    description: "The terms that govern the use of this website and the purchase of research materials.",
    sections: [
      { h: "Research Use Only", p: DISCLAIMER },
      { h: "Eligibility", p: "By ordering, you confirm that you are acquiring materials for laboratory research and analytical use only, and not for human or veterinary use." },
      { h: "Product Information", p: "We aim to present accurate, organized information. Live pricing, inventory, variants, and availability are determined by our connected store at the time of purchase." },
      { h: "Orders & Payment", p: "Orders, payment, taxes, and shipping are processed by our connected Shift4Shop store, subject to its checkout terms." },
      { h: "Limitation of Liability", p: "Products are provided for research use only. The customer is solely responsible for the safe, lawful, and appropriate handling and use of any material within a research setting." },
    ],
  },
  "accessibility": {
    overline: "Commitment",
    title: "Accessibility Statement",
    description: "Renova Peptide Co. is committed to an accessible, inclusive web experience.",
    sections: [
      { h: "Our Commitment", p: "We strive to follow WCAG best practices, including keyboard-accessible navigation, visible focus states, sufficient colour contrast, descriptive labels, and support for reduced-motion preferences." },
      { h: "Ongoing Effort", p: "Accessibility is an ongoing effort. We continue to review and improve the experience across devices and assistive technologies." },
      { h: "Feedback", p: "If you encounter an accessibility barrier on this site, please let us know through our Contact page so we can address it." },
    ],
  },
};

export default function PolicyPage({ type: typeProp }) {
  const params = useParams();
  const type = typeProp || params.type;
  const policy = POLICIES[type];

  useSEO({
    title: policy ? `${policy.title} | Renova Peptide Co.` : "Policy | Renova Peptide Co.",
    description: policy?.description || "",
    path: `/${type}`,
    jsonLd: policy
      ? breadcrumbSchema([{ name: "Home", path: "/" }, { name: policy.title, path: `/${type}` }])
      : null,
  });

  if (!policy) return <NotFound />;

  return (
    <>
      <PageHeader
        overline={policy.overline}
        title={policy.title}
        intro={policy.description}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: policy.title, path: `/${type}` }]}
      />
      <section className="container-rnv py-16 md:py-20">
        <div className="max-w-3xl">
          {policy.sections.map((s, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="mb-10">
                <h2 className="font-serif text-2xl md:text-3xl text-[#1E2A44]">{s.h}</h2>
                <p className="mt-3 text-[#262E3D] leading-relaxed">{s.p}</p>
              </div>
            </Reveal>
          ))}
          <div className="gold-rule my-8" />
          <p className="text-sm text-[#85827D]">
            Have a question about this policy?{" "}
            <Link to="/contact" className="text-[#1E2A44] underline">Contact us</Link> or{" "}
            <a href={STORE.account} className="text-[#1E2A44] underline">visit your store account</a>.
          </p>
        </div>
      </section>
    </>
  );
}
